import { Box, Divider, Grid, Tab, Tabs } from "@mui/material";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer.tsx";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch";
import {
  selectSorts,
  selectTabularFields,
  selectTabularGrouping,
} from "../../../../store/selectors/fieldsStateSelectors";
import { selectEditDisabled, selectSelectedBuilderTab } from "../../../../store/selectors/uiSettingsSelectors";
import type { BuilderTab } from "../../../../store/types/uiSettingsState.types";
import { createUiSettingsAction } from "../../../../store/utils/storeUtils";
import type { TableComponentProps } from "../../../viewer/common/Types.ts";
import TableDragLayer from "../../common/table/TableDragLayer.tsx";
import { getAvailableGroupFields } from "../../common/utilities/groupFieldStateHelper.ts";
import { getAvailableSortFields } from "../../common/utilities/sortFieldStateHelper.ts";
import ConditionsDropFieldsContainer from "../../pivot/table/conditions/ConditionsDropFieldsContainer.tsx";
import SettingsContainer from "../settings/SettingsContainer.tsx";
import FormatContainer from "./FormatContainer.tsx";
import GroupsDropFieldsContainer from "./GroupsDropFieldsContainer.tsx";
import SortDropsFieldsContainer from "./SortDropsFieldsContainer.tsx";
import TabularFieldsDropContainer from "./TabularFieldsDropContainer.tsx";

export const TableComponent = ({ ReportTypeSelector }: TableComponentProps) => {
  const dispatch = useAppDispatch();
  const selectedTab = useSelector(selectSelectedBuilderTab);
  const sortingAreaValues = useSelector(selectSorts);
  const fieldsAreaValues = useSelector(selectTabularFields);
  const groupingAreaValues = useSelector(selectTabularGrouping);
  const availableSortFields = useMemo(
    () => getAvailableSortFields(fieldsAreaValues, sortingAreaValues),
    [fieldsAreaValues, sortingAreaValues]
  );

  const availableGroupFields = useMemo(
    () => getAvailableGroupFields(fieldsAreaValues, groupingAreaValues),
    [fieldsAreaValues, groupingAreaValues]
  );
  const editDisabled = useSelector(selectEditDisabled);

  return (
    <Box sx={{ display: "flex", flex: 1, flexDirection: "column", gap: "1rem", mt: "1rem" }}>
      <Grid sx={{ px: "1.3rem" }}>
        {ReportTypeSelector}
        <Tabs
          value={selectedTab}
          sx={{ borderBottom: "none" }}
          onChange={(_, value: BuilderTab) => dispatch(createUiSettingsAction("setTab", value))}
        >
          <Tab value="Fields" label="Fields" />
          <Tab value="Settings" label="Settings" />
          <Tab value="Format" label="Format" />
        </Tabs>
      </Grid>
      <Divider sx={{ mt: -2 }} />
      {selectedTab === "Fields" && (
        <ScrollableFlexContainer
          scrollContainerSx={{ display: "flex", flexDirection: "column", gap: "1.5rem", px: "1.3rem" }}
        >
          <ConditionsDropFieldsContainer disableEqualitySelection={true} readonlyMode={editDisabled} />
          <TabularFieldsDropContainer readonlyMode={editDisabled} />
          <SortDropsFieldsContainer availableFields={availableSortFields} readonlyMode={editDisabled} />
          <GroupsDropFieldsContainer availableFields={availableGroupFields} readonlyMode={editDisabled} />
          <TableDragLayer />
        </ScrollableFlexContainer>
      )}
      {selectedTab === "Settings" && <SettingsContainer isApiReport={false} readonlyMode={editDisabled} />}
      {selectedTab === "Format" && <FormatContainer readonlyMode={editDisabled} />}
    </Box>
  );
};

export default TableComponent;
